import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSeoForPath } from '../config/routes';

const SITE_URL = 'https://www.colegioarce.com';
const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/7b2f5283-2e0b-4c29-9baf-4c93599e2df2/images/3b14e73d4efe8a6b9e189f480883417d3d75d534f34a1ecc9f9c16dbce620ccb.png';

export function SEOHead({ titleES, titleEN, descES, descEN, path, schema, breadcrumbs }) {
  const location = useLocation();
  const currentPath = path || location.pathname;
  const seo = getSeoForPath(currentPath);
  
  // Use route config SEO if available, fallback to props
  const lang = currentPath.startsWith('/en') ? 'en' : 'es';
  const title = seo?.title || (lang === 'es' ? titleES : titleEN) || 'Colegio ARCE';
  const description = seo?.description || (lang === 'es' ? descES : descEN) || '';
  const canonical = seo?.canonical || `${SITE_URL}${currentPath}`;
  const hreflang = seo?.hreflang || {};
  const locale = lang === 'es' ? 'es_ES' : 'en_US';

  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": lang === 'es' ? crumb.nameES : crumb.nameEN,
      "item": `${SITE_URL}${crumb.path}`
    }))
  } : null;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      
      {/* Hreflang */}
      {hreflang.es && <link rel="alternate" hrefLang="es" href={hreflang.es} />}
      {hreflang.en && <link rel="alternate" hrefLang="en" href={hreflang.en} />}
      {hreflang['x-default'] && <link rel="alternate" hrefLang="x-default" href={hreflang['x-default']} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={LOGO_URL} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content="Colegio ARCE" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={LOGO_URL} />
      
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
      {breadcrumbSchema && <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>}
    </Helmet>
  );
}


// Schema.org helpers (exported for pages)
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  "name": "Colegio ARCE",
  "url": "https://www.colegioarce.com",
  "logo": "https://static.prod-images.emergentagent.com/jobs/7b2f5283-2e0b-4c29-9baf-4c93599e2df2/images/3b14e73d4efe8a6b9e189f480883417d3d75d534f34a1ecc9f9c16dbce620ccb.png",
  "description": "Online Spanish language school for children and teens",
  "address": { "@type": "PostalAddress", "addressLocality": "Medford", "addressRegion": "MA", "addressCountry": "US" },
  "contactPoint": { "@type": "ContactPoint", "email": "laura@colegioarce.com", "contactType": "enrollment" }
};

export function buildArticleSchema(post) {
  return {
    "@context": "https://schema.org", "@type": "Article",
    "headline": post?.title || '', "description": post?.excerpt || '',
    "datePublished": post?.created_at, "dateModified": post?.updated_at,
    "publisher": { "@type": "Organization", "name": "Colegio ARCE" }
  };
}

export function buildOfferSchema() {
  return {
    "@context": "https://schema.org", "@type": "Offer",
    "name": "Colegio ARCE Enrollment", "priceCurrency": "USD",
    "price": "1495", "availability": "https://schema.org/InStock",
    "seller": { "@type": "Organization", "name": "Colegio ARCE" }
  };
}

export function buildPersonSchema(member) {
  return {
    "@context": "https://schema.org", "@type": "Person",
    "name": member?.name || '', "jobTitle": member?.role || '',
    "worksFor": { "@type": "Organization", "name": "Colegio ARCE" }
  };
}


export function buildCourseSchema(course) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course?.name || '',
    "description": course?.description || '',
    "provider": { "@type": "Organization", "name": "Colegio ARCE", "url": "https://www.colegioarce.com" },
    "educationalLevel": course?.age_range || '',
    "inLanguage": "es",
    "courseMode": "online",
    "hasCourseInstance": {
      "@type": "CourseInstance", "courseMode": "online",
      "duration": "P30W", "courseSchedule": { "@type": "Schedule", "repeatFrequency": "P1W", "duration": "PT50M" }
    }
  };
}

export function buildFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };
}
